import { createFileRoute } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	Image as ImageIcon,
	Layers,
	ShoppingBag,
	Sparkles,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/for/ecommerce";

export const Route = createFileRoute(path)({
	component: ForEcommerce,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher for Ecommerce - AI Social Media for DTC Brands",
			description:
				"DTC brands live on Instagram, TikTok, and Pinterest. AgentReacher schedules product posts, restocks, and campaigns with on-brand captions and timing.",
		}),
});

function ForEcommerce() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="For ecommerce"
				title="Sell more without posting more."
				description="Drop a product shot and AgentReacher ships your launches, restocks, and seasonal campaigns to Instagram, TikTok, Pinterest, and Facebook with on-brand captions. Approve the calendar, watch the queue run."
			/>

			<MarketingSection
				title="Built for DTC brands"
				description="Whether you run a brand of one or twenty SKUs, the workflow stays simple."
			>
				<FeatureGrid
					features={[
						{
							icon: ShoppingBag,
							title: "Product-aware drafts",
							description:
								"Drop a product image, the agent writes captions tuned to your brand and the platform.",
						},
						{
							icon: ImageIcon,
							title: "Carousels, Reels, pins",
							description:
								"All native formats supported. Same idea, every channel.",
						},
						{
							icon: Layers,
							title: "Cross-post Meta and Pinterest",
							description:
								"One product shot publishes to Instagram, Facebook, and Pinterest  -  with platform-specific captions.",
						},
						{
							icon: Bot,
							title: "Brand-voice consistency",
							description:
								"Train the agent on past posts so seasonal campaigns and restocks all sound like you.",
						},
						{
							icon: CalendarClock,
							title: "Campaign scheduling",
							description:
								"Plan a launch week, a Black Friday push, or a regular drop cadence in one calendar.",
						},
						{
							icon: Sparkles,
							title: "Drive from chat",
							description:
								"'Schedule the new collection across Instagram, Pinterest, and TikTok for Friday.' Done.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="A typical DTC workflow" align="left">
				<ol className="mx-auto max-w-2xl space-y-3 text-sm leading-relaxed text-muted-foreground">
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							1
						</span>
						<span>Connect Instagram, Facebook, Pinterest, and TikTok.</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							2
						</span>
						<span>
							Sync your media library or upload product shots directly.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							3
						</span>
						<span>Approve drafted captions per platform.</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							4
						</span>
						<span>
							Schedule a campaign or set a regular cadence. AgentReacher
							publishes natively.
						</span>
					</li>
				</ol>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Pro covers most DTC brands. Max if you operate multiple SKU brands
						or franchises.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
